/**
 * JS 端 XGBoost 在线训练器
 * 纯 JS 实现 - 浏览器本地跑
 *
 * 训练:
 *   F(x) = Σ η * tree_i(x)
 *   r = y - sigmoid(F)
 *   tree_i fit r
 */
import { calcFeatures } from './mlPredict.js';

const ETA = 0.15;
const MAX_DEPTH = 5;          // MAX v2: 4 → 5
const N_TREES = 60;           // MAX v2: 30 → 60
const MIN_CHILD = 25;
const SUBSAMPLE = 0.7;
const N_FEATURES = 23;        // MAX v2: 15 → 23

// 准备训练数据(只用最近 N 期,加快训练)
function buildTrainSet(history, maxPeriods = 400) {
  const N = history.length;
  const maxI = Math.min(N - 1, maxPeriods);
  const X = [];
  const y = [];
  for (let i = maxI; i > 0; i--) {
    const prev = history[i];
    const prev2 = (i + 1 < N) ? history[i + 1] : null;
    const target = history[i - 1];
    for (let a = 0; a < 10; a++) {
      for (let b = 0; b < 10; b++) {
        for (let c = 0; c < 10; c++) {
          X.push(calcFeatures(a, b, c, prev, prev2));
          y.push(a === target.a && b === target.b && c === target.c ? 1 : 0);
        }
      }
    }
  }
  return { X, y };
}

function sigmoid(x) {
  if (x > 30) return 0.9999999;
  if (x < -30) return 0.0000001;
  return 1 / (1 + Math.exp(-x));
}

function findBestSplit(rows, X, residuals) {
  let bestGain = -Infinity;
  let best = null;
  const totalSum = rows.reduce((s, i) => s + residuals[i], 0);
  const totalN = rows.length;

  for (let f = 0; f < N_FEATURES; f++) {
    const pairs = rows.map(i => [i, X[i][f]]).sort((a, b) => a[1] - b[1]);
    let leftSum = 0, leftN = 0;
    let rightSum = totalSum, rightN = totalN;
    for (let k = 0; k < pairs.length - 1; k++) {
      const [idx, val] = pairs[k];
      leftSum += residuals[idx]; leftN++;
      rightSum -= residuals[idx]; rightN--;
      if (val === pairs[k + 1][1]) continue;
      const gain = leftSum * leftSum / (leftN + 1) + rightSum * rightSum / (rightN + 1) - totalSum * totalSum / (totalN + 1);
      if (gain > bestGain) {
        bestGain = gain;
        best = {
          feature: f,
          threshold: (val + pairs[k + 1][1]) / 2,
          leftSum, rightSum, leftN, rightN,
        };
      }
    }
  }
  return best;
}

function makeLeaf(rows, residuals) {
  if (rows.length === 0) return { type: 'leaf', value: 0 };
  const val = rows.reduce((s, i) => s + residuals[i], 0) / (rows.length + 1);
  return { type: 'leaf', value: val };
}

function fitTreeWithDepth(X, residuals, rows, depthLeft) {
  if (!rows || rows.length === 0) {
    return { type: 'leaf', value: 0 };
  }
  if (depthLeft === 0 || rows.length < MIN_CHILD) {
    return makeLeaf(rows, residuals);
  }
  const best = findBestSplit(rows, X, residuals);
  if (!best) {
    return makeLeaf(rows, residuals);
  }
  const leftRows = rows.filter(i => X[i][best.feature] < best.threshold);
  const rightRows = rows.filter(i => X[i][best.feature] >= best.threshold);
  // 防御:任何一边为 0 都返回 leaf
  if (leftRows.length === 0 || rightRows.length === 0) {
    return makeLeaf(rows, residuals);
  }
  const leftChild = fitTreeWithDepth(X, residuals, leftRows, depthLeft - 1);
  const rightChild = fitTreeWithDepth(X, residuals, rightRows, depthLeft - 1);
  if (!leftChild || !rightChild) {
    return makeLeaf(rows, residuals);
  }
  return {
    type: 'split',
    feature: best.feature,
    threshold: best.threshold,
    yes: leftChild,
    no: rightChild,
  };
}

function predictTreeFast(tree, x) {
  let node = tree;
  while (node.type === 'split') {
    if (x[node.feature] < node.threshold) node = node.yes;
    else node = node.no;
  }
  return node.value;
}

export function trainOnline(history, options = {}) {
  const maxPeriods = options.maxPeriods || 400;
  const nTrees = options.nTrees || N_TREES;
  const maxDepth = options.maxDepth || MAX_DEPTH;
  const onProgress = options.onProgress || (() => {});

  onProgress({ stage: 'prep', message: '准备数据...' });
  const t0 = Date.now();
  const { X, y } = buildTrainSet(history, maxPeriods);
  onProgress({ stage: 'prep_done', message: `数据 ${X.length} 样本` });

  const N = X.length;
  const scores = new Float64Array(N);
  const trees = [];
  const allRows = Array.from({ length: N }, (_, i) => i);

  for (let t = 0; t < nTrees; t++) {
    const residuals = new Float64Array(N);
    for (let i = 0; i < N; i++) {
      residuals[i] = y[i] - sigmoid(scores[i]);
    }

    const subRows = SUBSAMPLE < 1
      ? allRows.filter(() => Math.random() < SUBSAMPLE)
      : allRows;
    if (subRows.length < 100) {
      onProgress({ stage: 'stop', message: `采样不足 @ 树 ${t}` });
      break;
    }

    const tree = fitTreeWithDepth(X, residuals, subRows, maxDepth);
    if (tree.type === 'leaf') {
      for (let i = 0; i < N; i++) scores[i] += ETA * tree.value;
    } else {
      for (let i = 0; i < N; i++) {
        scores[i] += ETA * predictTreeFast(tree, X[i]);
      }
    }
    trees.push(tree);

    if (t % 5 === 0 || t === nTrees - 1) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      onProgress({ stage: 'tree', t: t + 1, total: nTrees, elapsed });
    }
  }

  const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
  onProgress({ stage: 'done', elapsed, nTrees: trees.length });
  return {
    n_trees: trees.length,
    base_score: 0,
    trees,
    feature_names: [
      'a', 'b', 'c', 'sum', 'span', 'hewei', 'hit_a', 'hit_b', 'hit_c', 'repeat',
      'prev_sum', 'prev_span', 'prev_hewei', 'sum_diff', 'span_diff',
      'route012_a', 'route012_b', 'route012_c', 'route012_range',
      'odd_count', 'big_count', 'prev2_sum_diff', 'prev2_span_same'
    ],
  };
}
